// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { _throwInternal, arrForEach, dateTimeUtilsDuration, dumpObj, fieldRedaction, getDocument, getExceptionName, getLocation, isNullOrUndefined } from "@microsoft/applicationinsights-core-js";
import { getPerformance, isUndefined, isWebWorker, scheduleTimeout } from "@nevware21/ts-utils";
import { _DYN_CONFIG, _DYN_DURATION, _DYN_GET_ENTRIES_BY_TYPE, _DYN_GET_PERFORMANCE_TIMI16, _DYN_HREF, _DYN_IS_PERFORMANCE_TIMIN15, _DYN_IS_PERFORMANCE_TIMIN18, _DYN_LENGTH, _DYN_NAVIGATION_START, _DYN_POPULATE_PAGE_VIEW_P4, _DYN_PROPERTIES, _DYN_SEND_PAGE_VIEW_INTER2, _DYN_SEND_PAGE_VIEW_PERFO3, _DYN_SHOULD_COLLECT_DURAT17, _DYN_START_TIME, _DYN_TRACK_PAGE_VIEW } from "../../__DynamicConstants";
/**
 * Factory function to create a PageViewManager instance.
 * @param appInsights - Internal interface to send telemetry
 * @param overridePageViewDuration - Whether to override page view duration
 * @param core - App Insights core instance
 * @param pageViewPerformanceManager - Page view performance manager instance
 * @returns A new IPageViewManager instance.
 * @internal
 */
export function createPageViewManager(appInsights, overridePageViewDuration, core, pageViewPerformanceManager) {
    var _a;
    var queueTimer = null;
    var itemQueue = [];
    var pageViewPerformanceSent = false;
    var firstPageViewSent = false;
    var _logger;
    if (core) {
        _logger = core.logger;
    }
    function _flushChannels(isAsync) {
        if (core) {
            core.flush(isAsync, function () {
                // Event flushed, callback added to prevent promise creation
            });
        }
    }
    function _startTimer() {
        if (!queueTimer) {
            queueTimer = scheduleTimeout((function () {
                queueTimer = null;
                var allItems = itemQueue.slice(0);
                var doFlush = false;
                itemQueue = [];
                arrForEach(allItems, function (item) {
                    if (!item()) {
                        // Not processed so rescheduled
                        itemQueue.push(item);
                    }
                    else {
                        doFlush = true;
                    }
                });
                if (itemQueue[_DYN_LENGTH /* @min:%2elength */] > 0) {
                    _startTimer();
                }
                if (doFlush) {
                    // We process at least one item so flush the queue
                    _flushChannels(true);
                }
            }), 100);
        }
    }
    function _addQueue(cb) {
        itemQueue.push(cb);
        _startTimer();
    }
    return _a = {},
        _a[_DYN_TRACK_PAGE_VIEW /* @min:trackPageView */] = function (pageView, customProperties) {
            var name = pageView.name;
            if (isNullOrUndefined(name) || typeof name !== "string") {
                var doc = getDocument();
                name = pageView.name = doc && doc.title || "";
            }
            var uri = pageView.uri;
            if (isNullOrUndefined(uri) || typeof uri !== "string") {
                var location_1 = getLocation();
                uri = pageView.uri = location_1 && location_1[_DYN_HREF /* @min:%2ehref */] || "";
            }
            if (core && core[_DYN_CONFIG /* @min:%2econfig */]) {
                uri = pageView.uri = fieldRedaction(pageView.uri, core[_DYN_CONFIG /* @min:%2econfig */]);
            }
            if (!firstPageViewSent) {
                var perf = getPerformance();
                // Access the performance timing object
                var navigationEntries = (perf && perf[_DYN_GET_ENTRIES_BY_TYPE /* @min:%2egetEntriesByType */] && perf[_DYN_GET_ENTRIES_BY_TYPE /* @min:%2egetEntriesByType */]("navigation"));
                // Edge Case the navigation Entries may return an empty array and the timeOrigin is not supported on IE
                if (navigationEntries && navigationEntries[0] && !isUndefined(perf.timeOrigin)) {
                    // Get the value of loadEventStart
                    var loadEventStart = navigationEntries[0].loadEventStart;
                    pageView[_DYN_START_TIME /* @min:%2estartTime */] = new Date(perf.timeOrigin + loadEventStart);
                }
                else {
                    // calculate the start time manually
                    var duration_1 = ((customProperties || pageView[_DYN_PROPERTIES /* @min:%2eproperties */] || {})[_DYN_DURATION /* @min:%2eduration */] || 0);
                    pageView[_DYN_START_TIME /* @min:%2estartTime */] = new Date(new Date().getTime() - duration_1);
                }
                firstPageViewSent = true;
            }
            // case 1a. if performance timing is not supported by the browser, send the page view telemetry with the duration provided by the user. If the user
            // do not provide the duration, set duration to undefined
            // Also this is case 4
            if (!pageViewPerformanceManager[_DYN_IS_PERFORMANCE_TIMIN15 /* @min:%2eisPerformanceTimingSupported */]()) {
                appInsights[_DYN_SEND_PAGE_VIEW_INTER2 /* @min:%2esendPageViewInternal */](pageView, customProperties);
                _flushChannels(true);
                if (!isWebWorker()) {
                    // no navigation timing (IE 8, iOS Safari 8.4, Opera Mini 8 - see http://caniuse.com/#feat=nav-timing)
                    _throwInternal(_logger, 2 /* eLoggingSeverity.WARNING */, 25 /* _eInternalMessageId.NavigationTimingNotSupported */, "trackPageView: navigation timing API used for calculation of page duration is not supported in this browser. This page view will be collected without duration and timing info.");
                }
                return;
            }
            var pageViewSent = false;
            var customDuration;
            // if the performance timing is supported by the browser, calculate the custom duration
            var start = pageViewPerformanceManager[_DYN_GET_PERFORMANCE_TIMI16 /* @min:%2egetPerformanceTiming */]()[_DYN_NAVIGATION_START /* @min:%2enavigationStart */];
            if (start > 0) {
                customDuration = dateTimeUtilsDuration(start, +new Date);
                if (!pageViewPerformanceManager[_DYN_SHOULD_COLLECT_DURAT17 /* @min:%2eshouldCollectDuration */](customDuration)) {
                    customDuration = undefined;
                }
            }
            // if the user has provided duration, send a page view telemetry with the provided duration. Otherwise, if
            // overridePageViewDuration is set to true, send a page view telemetry with the custom duration calculated earlier
            var duration;
            if (!isNullOrUndefined(customProperties) &&
                !isNullOrUndefined(customProperties[_DYN_DURATION /* @min:%2eduration */])) {
                duration = customProperties[_DYN_DURATION /* @min:%2eduration */];
            }
            if (overridePageViewDuration || !isNaN(duration)) {
                if (isNaN(duration)) {
                    // case 3
                    if (!customProperties) {
                        customProperties = {};
                    }
                    customProperties[_DYN_DURATION /* @min:%2eduration */] = customDuration;
                }
                // case 2
                appInsights[_DYN_SEND_PAGE_VIEW_INTER2 /* @min:%2esendPageViewInternal */](pageView, customProperties);
                _flushChannels(true);
                pageViewSent = true;
            }
            // now try to send the page view performance telemetry
            var maxDurationLimit = 60000;
            if (!customProperties) {
                customProperties = {};
            }
            // Queue the event for processing
            _addQueue(function () {
                var processed = false;
                try {
                    if (pageViewPerformanceManager[_DYN_IS_PERFORMANCE_TIMIN18 /* @min:%2eisPerformanceTimingDataReady */]()) {
                        processed = true;
                        var pageViewPerformance = {
                            name: name,
                            uri: uri
                        };
                        pageViewPerformanceManager[_DYN_POPULATE_PAGE_VIEW_P4 /* @min:%2epopulatePageViewPerformanceEvent */](pageViewPerformance);
                        if (!pageViewPerformance.isValid && !pageViewSent) {
                            // If navigation timing gives invalid numbers, then go back to "override page view duration" mode.
                            // That's the best value we can get that makes sense.
                            customProperties[_DYN_DURATION /* @min:%2eduration */] = customDuration;
                            appInsights[_DYN_SEND_PAGE_VIEW_INTER2 /* @min:%2esendPageViewInternal */](pageView, customProperties);
                        }
                        else {
                            if (!pageViewSent) {
                                customProperties[_DYN_DURATION /* @min:%2eduration */] = pageViewPerformance.durationMs;
                                appInsights[_DYN_SEND_PAGE_VIEW_INTER2 /* @min:%2esendPageViewInternal */](pageView, customProperties);
                            }
                            if (!pageViewPerformanceSent) {
                                appInsights[_DYN_SEND_PAGE_VIEW_PERFO3 /* @min:%2esendPageViewPerformanceInternal */](pageViewPerformance, customProperties);
                                pageViewPerformanceSent = true;
                            }
                        }
                    }
                    else if (start > 0 && dateTimeUtilsDuration(start, +new Date) > maxDurationLimit) {
                        // if performance timings are not ready but we exceeded the maximum duration limit, just log a page view telemetry
                        // with the maximum duration limit. Otherwise, keep waiting until performance timings are ready
                        processed = true;
                        if (!pageViewSent) {
                            customProperties[_DYN_DURATION /* @min:%2eduration */] = maxDurationLimit;
                            appInsights[_DYN_SEND_PAGE_VIEW_INTER2 /* @min:%2esendPageViewInternal */](pageView, customProperties);
                        }
                    }
                }
                catch (e) {
                    _throwInternal(_logger, 1 /* eLoggingSeverity.CRITICAL */, 38 /* _eInternalMessageId.TrackPVFailedCalc */, "trackPageView failed on page load calculation: " + getExceptionName(e), { exception: dumpObj(e) });
                }
                return processed;
            });
        },
        _a.teardown = function (unloadCtx, unloadState) {
            if (queueTimer) {
                queueTimer.cancel();
                queueTimer = null;
                var allItems = itemQueue.slice(0);
                itemQueue = [];
                arrForEach(allItems, function (item) {
                    if (item()) {
                        // Item processed successfully
                    }
                });
            }
        },
        _a;
}
//# sourceMappingURL=PageViewManager.js.map